import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Mail } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SeoHead from "@/components/SeoHead";
import InternalLinkBlock from "@/components/InternalLinkBlock";
import { servicesBySlugs } from "@/data/services";
import { ARTICLES } from "@/data/articles";

const SITE = "https://soveu.nl";

/**
 * Shared layout for the /diensten/* service pages and /sectoren/* sector pages.
 * Content comes from data/services.js and data/sectors.js.
 */
export default function SeoPageLayout({
  entry,
  eyebrow,
  breadcrumb = [],
  schemaType = "Service",
  testid,
}) {
  const canonical = `${SITE}${entry.to}`;

  // Onthoud het best passende inzicht voor de homepage ("Uitgelicht wisselen").
  useEffect(() => {
    try {
      const slug = (entry.relatedArticles || [])[0];
      if (slug) localStorage.setItem("soveu:lastTopicArticle", slug);
    } catch (e) {
      // localStorage niet beschikbaar — stil overslaan
    }
  }, [entry]);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumb.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: `${SITE}${b.path}`,
    })),
  };

  const mainSchema = {
    "@context": "https://schema.org",
    "@type": schemaType,
    name: entry.h1,
    description: entry.seoDescription,
    url: canonical,
    provider: {
      "@type": "ProfessionalService",
      name: "SovEU",
      url: `${SITE}/`,
      email: "info@soveu.nl",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Utrecht",
        addressCountry: "NL",
      },
    },
    areaServed: { "@type": "Country", name: "Netherlands" },
  };

  const faqSchema =
    entry.faqs && entry.faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: entry.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

  const relatedServiceItems = servicesBySlugs(entry.relatedServices || []).map(
    (s) => ({ to: s.to, label: s.navLabel, summary: s.summary, eyebrow: "Dienst" })
  );

  const relatedArticleItems = (entry.relatedArticles || [])
    .map((slug) => ARTICLES.find((a) => a.slug === slug))
    .filter(Boolean)
    .map((a) => ({
      to: a.to,
      label: a.title,
      summary: a.summary,
      eyebrow: a.category,
    }));

  return (
    <div data-testid={testid} className="pt-[96px]">
      <SeoHead
        title={entry.seoTitle}
        description={entry.seoDescription}
        path={entry.to}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(mainSchema) }}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      {/* Hero */}
      <section className="section bg-white pb-10">
        <div className="container-soveu max-w-3xl">
          <FadeIn>
            <nav
              aria-label="Kruimelpad"
              className="text-[13px] text-[#6B7280] mb-5"
              data-testid="breadcrumb"
            >
              {breadcrumb.map((b, i) => (
                <span key={b.path}>
                  {i > 0 && <span className="mx-2">/</span>}
                  {i < breadcrumb.length - 1 ? (
                    <Link
                      to={b.path}
                      className="hover:underline underline-offset-4"
                      style={{ color: "#2D6F8F" }}
                    >
                      {b.name}
                    </Link>
                  ) : (
                    <span>{b.name}</span>
                  )}
                </span>
              ))}
            </nav>
            <div className="eyebrow mb-4">{eyebrow}</div>
            <h1 className="h1-display mb-6">{entry.h1}</h1>
            <div className="space-y-4 body-lg text-[#334155] leading-relaxed">
              {entry.intro.map((p) => (
                <p key={p.slice(0, 40)}>{p}</p>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Content sections */}
      <section className="bg-white pb-4">
        <div className="container-soveu max-w-3xl space-y-12">
          {entry.sections.map((s) => (
            <FadeIn key={s.h2}>
              <article
                className="border-t border-[#E5E9EF] pt-10"
                data-testid={`section-${s.h2.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <h2 className="h2-display mb-5">{s.h2}</h2>
                {s.paragraphs && (
                  <div className="space-y-4 text-[#334155] body-lg leading-relaxed mb-5">
                    {s.paragraphs.map((p) => (
                      <p key={p.slice(0, 40)}>{p}</p>
                    ))}
                  </div>
                )}
                {s.bullets && (
                  <ul className="space-y-2.5">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-[#334155] text-[15px] leading-relaxed"
                      >
                        <Check
                          size={17}
                          className="mt-1 flex-shrink-0"
                          style={{ color: "#2D6F8F" }}
                          strokeWidth={2}
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Praktijkvoorbeeld (geanonimiseerd) */}
      {entry.praktijkvoorbeeld && (
        <section className="section bg-white" data-testid="praktijkvoorbeeld">
          <div className="container-soveu max-w-3xl">
            <FadeIn>
              <article
                className="rounded-lg border border-[#E5E9EF] p-8 md:p-10"
                style={{ backgroundColor: "#F5F7FA" }}
              >
                <div
                  className="text-[12px] font-semibold tracking-[0.12em] uppercase mb-3"
                  style={{ color: "#2D6F8F" }}
                >
                  Praktijkvoorbeeld · geanonimiseerd
                </div>
                <h2 className="h3-display mb-5">
                  {entry.praktijkvoorbeeld.title}
                </h2>
                <div className="space-y-4 text-[#334155] body-lg leading-relaxed mb-6">
                  {entry.praktijkvoorbeeld.situatie.map((p) => (
                    <p key={p.slice(0, 40)}>{p}</p>
                  ))}
                </div>
                {entry.praktijkvoorbeeld.resultaat && (
                  <>
                    <div
                      className="text-[13px] font-semibold tracking-[0.06em] uppercase mb-3"
                      style={{ color: "#1E3A5F" }}
                    >
                      Resultaat
                    </div>
                    <ul className="space-y-2.5">
                      {entry.praktijkvoorbeeld.resultaat.map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-3 text-[#334155] text-[15px] leading-relaxed"
                        >
                          <Check
                            size={17}
                            className="mt-1 flex-shrink-0"
                            style={{ color: "#2D6F8F" }}
                            strokeWidth={2}
                          />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <p className="text-[13px] text-[#6B7280] mt-6 pt-5 border-t border-[#E5E9EF]">
                  Dit voorbeeld is geanonimiseerd en illustratief. Herkenbare
                  details zijn weggelaten.
                </p>
              </article>
            </FadeIn>
          </div>
        </section>
      )}

      {/* FAQ */}
      {entry.faqs && entry.faqs.length > 0 && (
        <section className="section bg-white" data-testid="page-faq">
          <div className="container-soveu max-w-3xl">
            <FadeIn className="mb-8">
              <div className="eyebrow mb-3">Veelgestelde vragen</div>
              <h2 className="h2-display">Vragen over {entry.h1.toLowerCase()}</h2>
            </FadeIn>
            <div className="space-y-6">
              {entry.faqs.map((f, i) => (
                <FadeIn key={f.q} delay={i * 60}>
                  <article
                    className="border-t border-[#E5E9EF] pt-6"
                    data-testid={`faq-${i + 1}`}
                  >
                    <h3
                      className="text-[18px] font-semibold mb-2"
                      style={{ color: "#1E3A5F" }}
                    >
                      {f.q}
                    </h3>
                    <p className="text-[#334155] leading-relaxed text-[15px]">
                      {f.a}
                    </p>
                  </article>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Relevante diensten */}
      <InternalLinkBlock
        eyebrow="Relevante diensten"
        title="Diensten die hierbij aansluiten"
        intro="Onderdelen van het SovEU aanbod die vaak samen met dit vraagstuk aan de orde komen."
        items={relatedServiceItems}
        background="#F5F7FA"
        testid="relevante-diensten"
      />

      {/* Gerelateerde inzichten */}
      <InternalLinkBlock
        eyebrow="Gerelateerde inzichten"
        title="Verder lezen"
        intro="Achtergrondartikelen over AI governance, datagovernance en digitale regie."
        items={relatedArticleItems}
        testid="gerelateerde-inzichten"
      />

      {/* CTA */}
      <section
        className="section"
        style={{ backgroundColor: "#1E3A5F" }}
        data-testid="page-cta"
      >
        <div className="container-soveu max-w-3xl text-center text-white">
          <FadeIn>
            <h2 className="h2-display text-white mb-5">{entry.ctaTitle}</h2>
            <p className="body-lg text-white/75 mb-8">{entry.ctaText}</p>
            <Link
              to={`/contact?onderwerp=${encodeURIComponent(entry.navLabel)}`}
              className="btn-primary btn-on-navy"
              data-testid="page-cta-contact"
            >
              {entry.ctaLabel}
              <ArrowRight size={18} />
            </Link>
            <address className="not-italic text-[15px] text-white/70 mt-8 leading-relaxed">
              <a
                href="mailto:info@soveu.nl"
                className="inline-flex items-center gap-2 hover:text-white transition-colors"
                data-testid="page-cta-email"
              >
                <Mail size={15} /> info@soveu.nl
              </a>
              <br />
              SovEU, Utrecht, Nederland
            </address>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
